import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { useEventTitles } from "@/hooks/useEventTitles";
import { extractEventIdFromPath, isEventPath } from "@/lib/analytics";
import { EventPreviewDialog } from "./EventPreviewDialog";

interface TopPagesChartProps {
  data: { target: string; count: number }[];
  isLoading: boolean;
}

export const TopPagesChart = ({ data, isLoading }: TopPagesChartProps) => {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  
  // Fetch event titles for any event paths
  const allPaths = data.map((item) => item.target);
  const { data: eventTitles = {} } = useEventTitles(allPaths);

  const chartConfig = {
    count: {
      label: "Views",
      color: "hsl(var(--primary))",
    },
  };

  // Format display names with event titles where applicable
  const formattedData = data.map((item) => {
    const eventId = extractEventIdFromPath(item.target);
    let displayName = item.target;
    
    if (eventId && eventTitles[eventId]) {
      displayName = eventTitles[eventId];
    }
    
    return {
      ...item,
      eventId,
      isEvent: isEventPath(item.target),
      displayName: displayName.length > 25 ? `${displayName.slice(0, 25)}...` : displayName,
      fullName: eventId && eventTitles[eventId] ? eventTitles[eventId] : item.target,
    };
  });

  const handleBarClick = (data: { eventId?: string | null }) => {
    if (data?.eventId) {
      setSelectedEventId(data.eventId);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for this period
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={formattedData} layout="vertical">
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  width={150}
                  tickLine={false}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  formatter={(value, _name, props) => [value, props.payload.fullName]}
                />
                <Bar 
                  dataKey="count" 
                  radius={[0, 4, 4, 0]}
                  onClick={(data) => handleBarClick(data)}
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="hsl(var(--primary))"
                      cursor={entry.isEvent ? "pointer" : "default"}
                      className={entry.isEvent ? "hover:opacity-80 transition-opacity" : ""}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <EventPreviewDialog
        eventId={selectedEventId}
        open={!!selectedEventId}
        onOpenChange={(open) => !open && setSelectedEventId(null)}
      />
    </>
  );
};
